/**
 * src/services/sorobanAuthCredentials.ts
 *
 * The single place GeoTrust interprets Soroban authorization credentials.
 *
 * Protocol 28 / CAP-71 widened `SorobanCredentialsType` from two arms to four:
 *
 *   sourceAccount            covered by the transaction envelope signature
 *   address                  legacy; the signed payload is NOT address-bound
 *   addressV2                CAP-71; the address is bound into the payload
 *   addressWithDelegates     CAP-71-01; address credentials plus a delegate tree
 *
 * Before SDK 17 the codebase assumed a single arm and hand-built the signature
 * preimage. That is no longer safe: `addressV2` and `addressWithDelegates` bind
 * the address into the payload, so a hand-built legacy preimage would produce a
 * signature the host rejects.
 *
 * SDK 17 ships the arm-aware primitives (`inspectAuthEntry`, `authorizeEntry`,
 * `buildAuthorizationEntryPreimage`), so this module deliberately does NOT
 * reimplement them. What it adds is the part the SDK cannot know: GeoTrust's
 * policy about which arms we are actually able to sign for, applied in one place
 * instead of being re-derived at each call site.
 *
 * Scope note: this covers credential semantics only — arm detection, address,
 * nonce, expiration, payload construction, and signature attachment. It is not
 * a general XDR abstraction, and nothing else should be added to it.
 */

import {
  authorizeEntry,
  inspectAuthEntry,
  xdr,
  type AuthEntryCredentialType,
} from '@stellar/stellar-sdk';

/** The credential arm of an authorization entry. All four are recognized. */
export type CredentialArm = AuthEntryCredentialType;

/** A read-only summary of one authorization entry's credentials. */
export interface AuthEntryDescription {
  arm: CredentialArm;
  /** The authorizing address as a strkey, or null for source-account credentials. */
  address: string | null;
  /** The credential nonce, or null for source-account credentials. */
  nonce: bigint | null;
  /** Exclusive ledger sequence the signature is valid until, or null for source-account. */
  signatureExpirationLedger: number | null;
}

/**
 * Arms GeoTrust can currently produce a signature for.
 *
 * Recognition and signing support are deliberately separate. `addressWithDelegates`
 * is recognized everywhere in this module but is NOT signable: CAP-71-01 delegate
 * trees require deciding which delegate nodes sign and assembling the delegate
 * set, which GeoTrust does not model. Treating it as an ordinary address
 * credential would attach a signature to the top-level node only and produce an
 * entry the host rejects — or, worse, one that authorizes something other than
 * what the operator reviewed.
 */
const SIGNABLE_ARMS: readonly CredentialArm[] = ['address', 'addressV2'];

export function isSignableArm(arm: CredentialArm): boolean {
  return SIGNABLE_ARMS.includes(arm);
}

export function describeAuthEntry(
  entry: xdr.SorobanAuthorizationEntry,
): AuthEntryDescription {
  const info = inspectAuthEntry(entry);
  return {
    arm: info.credentialType,
    address: info.address,
    nonce: info.nonce,
    signatureExpirationLedger: info.signatureExpirationLedger,
  };
}

/**
 * Throws unless the arm is one GeoTrust can sign. Fails loudly and specifically
 * rather than silently signing an arm with different semantics.
 */
export function assertSignableArm(arm: CredentialArm, context: string): void {
  if (isSignableArm(arm)) return;

  if (arm === 'addressWithDelegates') {
    throw new Error(
      `${context}: this authorization entry uses delegated credentials ` +
      '(SOROBAN_CREDENTIALS_ADDRESS_WITH_DELEGATES), which GeoTrust cannot sign yet. ' +
      'Signing it as an ordinary address credential would not satisfy the delegate ' +
      'signatures the account requires.',
    );
  }

  throw new Error(
    `${context}: authorization entry uses ${arm} credentials, which carry no ` +
    'address signature. It cannot be signed as an address credential.',
  );
}

/**
 * Index of the entry authorizing `address`, or -1.
 *
 * Matches on the decoded strkey so it works for every address-bearing arm,
 * rather than only the legacy one.
 */
export function findAuthEntryIndexForAddress(
  entries: readonly xdr.SorobanAuthorizationEntry[],
  address: string,
): number {
  return entries.findIndex((entry) => {
    try {
      return describeAuthEntry(entry).address === address;
    } catch {
      return false;
    }
  });
}

/**
 * Authorize an entry with a caller-built signature value, for smart accounts
 * whose `__check_auth` expects a custom structure (GeoTrust's passkey/WebAuthn
 * accounts).
 *
 * `buildSignature` receives the 32-byte signing payload — the hash of the
 * arm-correct preimage, built by the SDK — which is what a WebAuthn challenge
 * must be bound to.
 *
 * Returns a signed copy; SDK 17 credential values are immutable.
 */
export async function authorizeEntryWithCustomSignature(
  entry: xdr.SorobanAuthorizationEntry,
  validUntilLedgerSeq: number,
  networkPassphrase: string,
  buildSignature: (payload: Uint8Array) => Promise<xdr.ScVal>,
  context = 'Authorization',
): Promise<xdr.SorobanAuthorizationEntry> {
  assertSignableArm(describeAuthEntry(entry).arm, context);

  return authorizeEntry(
    entry,
    async (_preimage, payload) => ({ signatureScVal: await buildSignature(payload) }),
    validUntilLedgerSeq,
    networkPassphrase,
  );
}

/**
 * Authorize an entry with a classic Ed25519 keypair. Thin wrapper over the SDK
 * that applies the same arm policy as the custom-signature path.
 *
 * Returns a signed copy; SDK 17 credential values are immutable.
 */
export async function authorizeEntryWithKeypair(
  entry: xdr.SorobanAuthorizationEntry,
  keypair: Parameters<typeof authorizeEntry>[1],
  validUntilLedgerSeq: number,
  networkPassphrase: string,
  context = 'Authorization',
): Promise<xdr.SorobanAuthorizationEntry> {
  assertSignableArm(describeAuthEntry(entry).arm, context);
  return authorizeEntry(entry, keypair, validUntilLedgerSeq, networkPassphrase);
}
